import { useCallback, useMemo } from 'react';
import { Table, InputNumber, Button, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

interface TierRow {
  _id: number;
  maxUsers: number;
  expiryDays: number;
}

interface TierListEditorProps {
  value: string;
  onChange: (json: string) => void;
  disabled?: boolean;
}

let nextId = 0;

function parseTiers(val: string): TierRow[] {
  try {
    const parsed = JSON.parse(val);
    if (Array.isArray(parsed)) {
      return parsed.map((item: { maxUsers?: number; expiryDays?: number }) => ({
        _id: ++nextId,
        maxUsers: Number(item.maxUsers) || 0,
        expiryDays: Number(item.expiryDays) || 0,
      }));
    }
  } catch {
    // ignore
  }
  return [];
}

export function TierListEditor({
  value,
  onChange,
  disabled = false,
}: TierListEditorProps) {
  const rows = useMemo(() => parseTiers(value), [value]);

  const emitChange = useCallback(
    (next: TierRow[]) => {
      const sorted = [...next].sort((a, b) => a.maxUsers - b.maxUsers);
      const serialized = sorted.map(({ maxUsers, expiryDays }) => ({
        maxUsers,
        expiryDays,
      }));
      onChange(JSON.stringify(serialized));
    },
    [onChange],
  );

  const handleCellChange = useCallback(
    (id: number, field: 'maxUsers' | 'expiryDays', numVal: number | null) => {
      const next = rows.map((r) =>
        r._id === id ? { ...r, [field]: numVal ?? 0 } : r,
      );
      emitChange(next);
    },
    [rows, emitChange],
  );

  const handleDelete = useCallback(
    (id: number) => {
      emitChange(rows.filter((r) => r._id !== id));
    },
    [rows, emitChange],
  );

  const handleAdd = useCallback(() => {
    emitChange([...rows, { _id: ++nextId, maxUsers: 0, expiryDays: 0 }]);
  }, [rows, emitChange]);

  const columns: ColumnsType<TierRow> = [
    {
      title: 'Max utilisateurs',
      dataIndex: 'maxUsers',
      key: 'maxUsers',
      width: 180,
      render: (_: unknown, record: TierRow) => (
        <InputNumber
          value={record.maxUsers}
          onChange={(n) => handleCellChange(record._id, 'maxUsers', n)}
          min={1}
          disabled={disabled}
          controls={false}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Expiration (jours)',
      dataIndex: 'expiryDays',
      key: 'expiryDays',
      width: 180,
      render: (_: unknown, record: TierRow) => (
        <InputNumber
          value={record.expiryDays}
          onChange={(n) => handleCellChange(record._id, 'expiryDays', n)}
          min={1}
          disabled={disabled}
          controls={false}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, record: TierRow) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          disabled={disabled}
          onClick={() => handleDelete(record._id)}
        />
      ),
    },
  ];

  const hasInvalid = rows.some((r) => r.maxUsers <= 0 || r.expiryDays <= 0);
  const hasDuplicate = (() => {
    const seen = new Set<number>();
    for (const r of rows) {
      if (r.maxUsers > 0) {
        if (seen.has(r.maxUsers)) return true;
        seen.add(r.maxUsers);
      }
    }
    return false;
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="_id"
        pagination={false}
        size="small"
        bordered
      />
      {(hasInvalid || hasDuplicate) && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {hasInvalid && 'Tous les paliers doivent avoir des valeurs > 0. '}
          {hasDuplicate && 'Les maxUsers doivent être uniques.'}
        </Text>
      )}
      <Button
        icon={<PlusOutlined />}
        size="small"
        disabled={disabled}
        onClick={handleAdd}
        style={{ alignSelf: 'flex-start' }}
      >
        Ajouter un palier
      </Button>
    </div>
  );
}
